import { SelfServiceError } from '@/Services/BKAPI/Errors/SelfServiceError';
import { mapCancellationOptions } from '@/Services/BKAPI/Reducers/OrderDetailsReducer';
import {
    FLEX_TICKET_STATUS,
    ORDER_ITEM_TYPE,
    Order,
    OrderItem,
    OrderItemTicket,
    OrderItemTicketStatus,
    OrderItemType,
} from '@/Services/BKAPI/models';
import type { Locale } from '@/constants/locales';
import { SelfServiceOrderResponseOrderItemsInnerOrderItemTypeEnum as ApiOrderItemType } from '@bookingkit/api-v4';
import type { SelfServiceOrderResponse } from '@bookingkit/api-v4';
import type { SelfServiceOrderResponseOrderItemsInner } from '@bookingkit/api-v4/models/self-service-order-response-order-items-inner';
import type { SelfServiceOrderTicketResponseTicketsInner } from '@bookingkit/api-v4/models/self-service-order-ticket-response-tickets-inner';

// Generic order mapping: no order-item-type filtering, tickets optional. Use this for any flow
// that needs the order shape without the season-pass/personalisation requirements that
// `fromAPIToOrderDetails` enforces (which requires SEASON_PASS items with at least one ticket).
// Explicit mapping from the API's order_item_type onto the domain OrderType, so SDK enum
// changes are absorbed here rather than leaking `as` casts across the codebase.
const ORDER_TYPE_BY_API: Record<string, OrderItemType> = {
    [ApiOrderItemType.Ticket]: ORDER_ITEM_TYPE.TICKET,
    [ApiOrderItemType.SeasonPass]: ORDER_ITEM_TYPE.SEASON_PASS,
    [ApiOrderItemType.SeasonPassRenewal]: ORDER_ITEM_TYPE.SEASON_PASS_RENEWAL,
    [ApiOrderItemType.FlexibleTicketPurchase]:
        ORDER_ITEM_TYPE.FLEXIBLE_TICKET_PURCHASE,
    [ApiOrderItemType.OpenDateTicketPurchase]:
        ORDER_ITEM_TYPE.OPEN_DATE_TICKET_PURCHASE,
    // VOUCHER_PURCHASE arrives in the response since FEE-2218 but the SDK enum
    // doesn't yet include it; widen by string literal so the bump becomes a
    // no-op delete of this comment.
    VOUCHER_PURCHASE: ORDER_ITEM_TYPE.VOUCHER_PURCHASE,
};

export const toOrderType = (
    apiType: SelfServiceOrderResponseOrderItemsInner['order_item_type']
): OrderItemType =>
    apiType ? ORDER_TYPE_BY_API[apiType] : ORDER_ITEM_TYPE.TICKET;

const reduceTickets = (
    tickets: SelfServiceOrderTicketResponseTicketsInner[] = []
): OrderItemTicket[] =>
    tickets
        .filter((ticket) => Boolean(ticket.id && ticket.code && ticket.status))
        .map((ticket) => ({
            id: ticket.id as string,
            code: ticket.code as string,
            status: ticket.status as OrderItemTicketStatus,
        }));

const reduceOrderItems = (
    orderItems: SelfServiceOrderResponseOrderItemsInner[] = []
): OrderItem[] =>
    orderItems.map((item, index) => {
        if (item.id == null) {
            throw SelfServiceError.fromMissingProperty(
                `order_items[${index}].id`,
                'string',
                item.id
            );
        }
        return {
            type: toOrderType(item.order_item_type),
            id: item.id,
            name: item.offer_name ?? '',
            status:
                item.status === 'ACCEPTED'
                    ? FLEX_TICKET_STATUS.ACTIVE
                    : FLEX_TICKET_STATUS.CANCELLED,
            tickets: reduceTickets(item.tickets),
            restrictions: { ticketHolderInfo: [] },
            cancellationOptions: mapCancellationOptions(
                (item as { cancellation_options?: unknown }).cancellation_options
            ),
        };
    });

export const fromAPIToOrder = (
    orderResponse: SelfServiceOrderResponse
): Order => {
    const { id, code, locale, order_items } = orderResponse;
    if (id == null) {
        throw SelfServiceError.fromMissingProperty('id', 'string', id);
    }
    if (code == null) {
        throw SelfServiceError.fromMissingProperty('code', 'string', code);
    }
    return {
        id,
        code,
        locale: locale as Locale,
        orderItems: reduceOrderItems(order_items ?? []),
    };
};
